import { Row, Table, flexRender } from '@tanstack/react-table';
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import EmptyTable from '../../../icons/svg/empty-table.svg';
import { SortableTableHeader } from './sortable-header';
import { scrollableTableContainerSx } from './utils';

/**
 * The container styling both card tables share.
 *
 * Side padding sets the table's minimum width. At 8px the widest of them still
 * fits the narrowest content area, and auto-layout spreads the slack back out.
 * The sticky header has to outrank the shared Button, which paints at z-10.
 *
 * `extra` is for rules only one table needs, so the shared part stays one copy.
 */
export const cardTableContainerSx = (extra: Record<string, unknown> = {}) => ({
  ...(scrollableTableContainerSx() as Record<string, unknown>),
  '& .MuiTableHead-root': {
    position: 'sticky',
    top: 0,
    zIndex: 11,
    backgroundColor: 'white'
  },
  '& .MuiTableCell-root': {
    paddingLeft: '8px',
    paddingRight: '8px'
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    paddingTop: '8px',
    paddingBottom: '8px'
  },
  ...extra
});

type Props<T> = {
  table: Table<T>;
  containerSx: Record<string, unknown>;
  emptyTitle: string;
  emptyText: string;
  /** Per-row classes, where a table marks rows the manager still has to act on. */
  rowClassName?: (row: Row<T>) => string;
};

/**
 * The scrollable table body both pxtra Card tables render.
 *
 * They differ only in what their empty state says and whether a row carries
 * styling of its own — everything from the header groups down to the cells is
 * the same, so it lives here rather than in both.
 */
export const CardTable = <T,>({
  table,
  containerSx,
  emptyTitle,
  emptyText,
  rowClassName
}: Props<T>) => (
  <TableContainer sx={containerSx}>
    <MuiTable>
      <TableHead>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <SortableTableHeader key={header.id} header={header} />
            ))}
          </TableRow>
        ))}
      </TableHead>
      <TableBody>
        {table.getRowModel().rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={table.getVisibleLeafColumns().length} className="!border-none">
              <div className="flex flex-col items-center justify-center py-8 gap-y-2">
                <img src={EmptyTable} className="w-64 h-32" alt="Empty Table" />
                <div className="font-medium text-base">{emptyTitle}</div>
                <div className="text-grey-600 text-sm">{emptyText}</div>
              </div>
            </TableCell>
          </TableRow>
        ) : (
          table.getRowModel().rows.map((row) => (
            <TableRow key={row.id} className={rowClassName?.(row) ?? 'hover:bg-heaven-100'}>
              {row.getVisibleCells().map((cell) => (
                <TableCell className="py-2 border-grey-100" key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        )}
      </TableBody>
    </MuiTable>
  </TableContainer>
);
